import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, ArrowRight, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { clearPublisherToken } from "@/lib/publisherApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Anonymous client — magic-link request needs NO cookies (it's a public form
// post). Skipping `withCredentials` keeps the browser out of strict CORS mode,
// which sidesteps the `Access-Control-Allow-Origin: *` + credentials conflict
// that some CDN/ingress configs introduce in front of the backend.
const apiClient = axios.create({ baseURL: API, timeout: 15000 });

/**
 * Passwordless publisher login.
 * Verified app owners enter their email → backend emails them a one-time
 * magic link → clicking it sets a 30-day session cookie and redirects to
 * /publisher/dashboard.
 */
const PublisherLoginPage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !email.includes("@")) {
      setError(isNl ? "Vul een geldig e-mailadres in." : "Please enter a valid email address.");
      return;
    }
    setBusy(true);
    // Any stale token from a previous expired session would silently auto-
    // attach to publisher requests and mask new sign-ins as "already logged
    // in" — wipe it before starting a fresh magic-link flow.
    clearPublisherToken();
    try {
      await apiClient.post("/publisher/login/request", { email: email.trim() });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 429) {
        setError(isNl
          ? "Te veel inlog-pogingen. Probeer over 15 minuten opnieuw — of check je inbox, mogelijk staat er nog een geldige link van een eerdere aanvraag."
          : (detail || "Too many login requests. Try again in 15 minutes — or check your inbox, an earlier link may still be valid."));
      } else if (status >= 400 && status < 500 && detail) {
        setError(detail);
      } else if (!err?.response) {
        // No HTTP response received at all (network/CORS/timeout). Most
        // common real-world cause: a previous request was rate-limited
        // and the user retried before the 15-min cooldown expired.
        setError(isNl
          ? "Kan de server niet bereiken. Mogelijke oorzaken: (1) je hebt net al een sign-in link aangevraagd — check je inbox; (2) je internet-verbinding hapert; (3) probeer over 15 minuten opnieuw."
          : "Couldn't reach the server. Likely causes: (1) you just requested a sign-in link — check your inbox; (2) connectivity hiccup; (3) try again in 15 minutes.");
      } else {
        setError(detail || err?.message || (isNl ? "Er ging iets mis." : "Something went wrong."));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-120px)] bg-[#F8FAFC] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center mb-8">
          <span className="font-heading text-2xl text-[#0F172A]">Horse<span className="text-[#2563EB]">App</span>Compare</span>
        </Link>

        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 bg-[#EFF6FF] text-[#2563EB] rounded-lg flex items-center justify-center">
              <ShieldCheck size={18} />
            </div>
            <div className="overline text-[#64748B]">{isNl ? "Publisher-portaal" : "Publisher portal"}</div>
          </div>
          <h1 className="font-heading text-2xl text-[#0F172A] mb-2">
            {isNl ? "Inloggen" : "Sign in"}
          </h1>
          <p className="text-sm text-[#64748B] mb-6 leading-relaxed">
            {isNl
              ? "We sturen je een eenmalige inloglink naar het geverifieerde e-mailadres dat aan jouw app gekoppeld is."
              : "We'll email a one-time sign-in link to the verified address linked to your app."}
          </p>

          {sent ? (
            <div className="text-center py-2" data-testid="publisher-login-sent">
              <div className="w-12 h-12 mx-auto bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 size={24} />
              </div>
              <h2 className="font-heading text-lg text-[#0F172A] mb-2">
                {isNl ? "Check je inbox" : "Check your inbox"}
              </h2>
              <p className="text-sm text-[#64748B] leading-relaxed mb-4">
                {isNl
                  ? <>Als <strong className="text-[#0F172A]">{email}</strong> aan een geverifieerde app gekoppeld is, vind je daar een inloglink. Geldig 20 minuten.</>
                  : <>If <strong className="text-[#0F172A]">{email}</strong> is linked to a verified app, you{`'`}ll find a sign-in link there. Valid for 20 minutes.</>}
              </p>
              <button
                type="button"
                onClick={() => { setSent(false); setEmail(""); }}
                className="text-xs text-[#2563EB] hover:underline"
                data-testid="publisher-login-back"
              >
                {isNl ? "Ander e-mailadres gebruiken" : "Use a different email"}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="publisher-login-form">
              <div>
                <label htmlFor="pub-email" className="overline block mb-1.5 text-[#64748B]">{isNl ? "E-mailadres" : "Email address"}</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                  <input
                    id="pub-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={isNl ? "jij@jouwapp.com" : "you@yourapp.com"}
                    className="w-full pl-9 pr-3 py-2.5 bg-white border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20"
                    data-testid="publisher-login-email"
                  />
                </div>
              </div>

              {error && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="publisher-login-error">{error}</div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white font-medium rounded-full px-5 py-2.5 transition-colors"
                data-testid="publisher-login-submit"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                {busy
                  ? (isNl ? "Versturen…" : "Sending…")
                  : (isNl ? "Stuur inloglink" : "Send sign-in link")}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-[#94A3B8] mt-6 leading-relaxed">
          {isNl
            ? <>Nog geen geverifieerde app? <Link to="/apps" className="text-[#2563EB] hover:underline">Vind je app</Link> en klik op {`"Claim this app"`}.</>
            : <>Don{`'`}t have a verified app yet? <Link to="/apps" className="text-[#2563EB] hover:underline">Find your app</Link> and click {`"Claim this app"`}.</>}
        </p>
      </div>
    </main>
  );
};

export default PublisherLoginPage;
